import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { almacen, newOperationId, type WasteReason } from '../api';
import { Buscable, CuadroDeBusqueda } from '../Buscable';
import { filtrarFilas, opcionDeInsumo } from '../busqueda';
import { cuantoFalta, fechaDeCalendario } from '../vencimiento';
import {
  Boton,
  Cabecera,
  Cargando,
  Cifra,
  ConAyuda,
  Fallo,
  Pastilla,
  Rotulo,
  Tarjeta,
  Vacio,
} from '../componentes';
import {
  motivosDeMerma,
  MOTIVO_DE_MERMA,
  cantidad,
  dia,
  diaDeLima,
  limitesDelMes,
  pideFirma,
  soles,
} from '../formato';
import { tieneModulo } from '../negocio';
import { mayuscula, useNegocio, useVocabulario } from '../vocabulario';

/**
 * Dar de baja lo que se perdió, con su motivo y su responsable.
 *
 * El motivo no es opcional y la lista es cerrada. Con texto libre, la merma del
 * mes acaba siendo un montón de frases distintas para lo mismo, y sin poder
 * sumarla por causa nadie sabe si el problema es la cámara, el proveedor o la
 * cocina.
 */
/** El lote de `#/mermas/baja/<lote>`, o nulo si se entró a Mermas sin más. */
function loteDeLaUrl(): string | null {
  const tramos = window.location.hash.replace('#/', '').split('/');
  return tramos[1] === 'baja' && (tramos[2] ?? '') !== '' ? tramos[2]! : null;
}

export function Mermas() {
  const vocabulario = useVocabulario();
  const negocio = useNegocio();
  const clientes = useQueryClient();

  const insumos = useQuery({ queryKey: ['insumos'], queryFn: almacen.supplies });

  /*
   * El mes, en Lima. La cifra decía «merma registrada» y sumaba los últimos
   * doscientos movimientos, fueran de cuando fueran: no era la del mes ni la
   * de nada, y comparar un mes con otro era imposible.
   */
  const [mes, setMes] = useState(() => diaDeLima().slice(0, 7));
  const { desde, hasta } = limitesDelMes(mes);
  const mermas = useQuery({
    queryKey: ['movimientos', 'waste', mes],
    queryFn: () => almacen.movements({ kind: 'waste', from: desde, to: hasta, limit: 500 }),
  });

  const [supplyId, setSupplyId] = useState('');
  const [texto, setTexto] = useState('');
  const [lotId, setLotId] = useState('');
  const [cuanto, setCuanto] = useState('');
  const [motivo, setMotivo] = useState<WasteReason>('natural_decay');
  const [pin, setPin] = useState('');
  const [firmadoPor, setFirmadoPor] = useState<string | null>(null);

  /*
   * El mismo identificador en cada intento. Si la red se corta después de
   * guardarse y alguien vuelve a pulsar, el servidor reconoce la operación y
   * no da de baja dos veces lo mismo. Se renueva al terminar bien.
   */
  const [operationId, setOperationId] = useState(newOperationId);

  /*
   * El lote que se pidió dar de baja desde Lotes o el Resumen.
   *
   * Llega como `#/mermas/baja/<lote>` y deja el formulario hecho: el insumo,
   * el lote, todo lo que queda y el motivo. Quien lo tira solo revisa y firma.
   * Antes eran cuatro pasos en otra pantalla para tirar lo que el sistema ya
   * sabía que había que tirar.
   */
  const [loteDeBaja] = useState(loteDeLaUrl);
  const [loteCargado, setLoteCargado] = useState<{ code: string; supply: string } | null>(null);
  const todosLosLotes = useQuery({
    queryKey: ['lotes', 'baja', loteDeBaja],
    queryFn: () => almacen.lots(),
    enabled: loteDeBaja !== null,
  });

  useEffect(() => {
    if (loteDeBaja === null || loteCargado !== null) return;
    const lote = todosLosLotes.data?.find((uno) => uno.id === loteDeBaja);
    if (lote === undefined) return;

    setSupplyId(lote.supplyId);
    setLotId(lote.id);
    // «12», no «12,000», que aquí se lee doce mil; y sin separador de miles,
    // que al leerlo otra vez como importe se confundiría con decimales.
    setCuanto(String(Number.parseFloat(lote.quantity)));
    setMotivo(lote.expiry === 'expired' ? 'expired' : 'natural_decay');
    setLoteCargado({ code: lote.code, supply: lote.supply });
    // Que recargar la página no lo vuelva a rellenar después de darlo de baja.
    window.history.replaceState(null, '', '#/mermas');
  }, [loteDeBaja, loteCargado, todosLosLotes.data]);

  const registrar = useMutation({
    mutationFn: async () => {
      /*
       * La firma va antes y en la misma pantalla. Quien registra es quien tiene
       * el táper delante; quien responde llega, teclea su PIN y se va. Así el
       * rastro tiene las dos personas de verdad.
       */
      let authorizationId: string | null = null;
      if (pideFirma(motivo)) {
        const firma = await almacen.authorize({
          operationId,
          action: 'inventory.waste',
          pin: pin.trim(),
        });
        authorizationId = firma.authorizationId;
        setFirmadoPor(firma.authorizedBy.fullName);
      }

      return almacen.registerWaste({
        operationId,
        supplyId,
        lotId: lotId === '' ? null : lotId,
        quantity: cuanto,
        reason: motivo,
        authorizationId,
      });
    },
    onSuccess: () => {
      setCuanto('');
      setPin('');
      setLotId('');
      setOperationId(newOperationId());
      void clientes.invalidateQueries();
    },
  });

  const delMes = (mermas.data ?? []).reduce(
    (suma, movimiento) => suma + Math.abs(Number.parseFloat(movimiento.value)),
    0,
  );

  const elegido = (insumos.data ?? []).find((insumo) => insumo.id === supplyId);

  // El lote, cuando se sabe cuál se tira: el táper vencido no es siempre el
  // que el sistema cree que vence antes.
  const lotes = useQuery({
    queryKey: ['lotes', supplyId],
    queryFn: () => almacen.lots(supplyId),
    enabled: supplyId !== '' && (elegido?.isLotTracked ?? false),
  });
  const puedeRegistrar =
    supplyId !== '' &&
    cuanto.trim() !== '' &&
    (!pideFirma(motivo) || /^\d{4,8}$/.test(pin.trim())) &&
    !registrar.isPending;

  return (
    <>
      <Cabecera
        titulo="Mermas y bajas"
        subtitulo="Pérdidas registradas con motivo y responsable" ayuda={`Todo lo que se pierde, con su motivo y quién lo registró. Sirve para saber si el problema es ${vocabulario.dondeSePierde}.`}
        acciones={
          <div className="campo campo--en-linea">
            <Rotulo htmlFor="mermas-mes" ayuda="El mes que se mira. Las cifras y la lista son solo de ese mes.">Mes</Rotulo>
            <input
              id="mermas-mes"
              type="month"
              value={mes}
              max={diaDeLima().slice(0, 7)}
              onChange={(e) => e.target.value !== '' && setMes(e.target.value)}
            />
          </div>
        }
      />

      <div className="cuerpo">
        <div className="cifras">
          <Cifra
            rotulo="Merma del mes"
            ayuda="Lo que costó todo lo perdido en el mes elegido."
            valor={soles(delMes)}
            apoyo={`${(mermas.data ?? []).length} registros`}
          />
          <Cifra
            rotulo="Principal motivo"
            ayuda="El motivo que más dinero se llevó en el mes, no el que más veces aparece."
            valor={motivoMasFrecuente(mermas.data ?? [])}
            apoyo="de lo perdido"
            tono={(mermas.data ?? []).length > 0 ? 'aviso' : 'neutro'}
          />
        </div>

        <div className="dos-columnas">
          <div className="tabla-envoltura">
            {(mermas.data ?? []).length > 0 ? (
              <div className="filtros">
                <CuadroDeBusqueda
                  valor={texto}
                  onCambio={setTexto}
                  placeholder={`Buscar ${vocabulario.insumo}, lote, motivo o quién…`}
                  etiquetaAccesible="Buscar merma"
                />
              </div>
            ) : null}
            {mermas.isLoading ? (
              <Cargando que="las mermas" />
            ) : (mermas.data ?? []).length === 0 ? (
              <Vacio
                titulo="Ninguna merma registrada"
                mensaje="Aquí queda todo lo que se pierde, con su motivo y quién lo registró. Un almacén sin mermas registradas no es un almacén sin mermas: es uno que no las apunta."
              />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>{mayuscula(vocabulario.insumo)}</th>
                    <th className="numero">Cantidad</th>
                    <th>Motivo</th>
                    <th><ConAyuda ayuda="Quién registró la pérdida.">Responsable</ConAyuda></th>
                    <th>Fecha</th>
                    <th className="numero"><ConAyuda ayuda="Lo que costó lo perdido, al costo promedio.">Costo</ConAyuda></th>
                  </tr>
                </thead>
                <tbody>
                  {filtrarFilas(mermas.data ?? [], texto, (merma) => [
                    merma.supply,
                    merma.lot,
                    merma.performedBy,
                    MOTIVO_DE_MERMA[merma.reason as WasteReason] ?? merma.reason,
                  ]).map((merma) => (
                    <tr key={merma.id}>
                      <td>
                        <div className="celda-nombre__texto">
                          <strong>{merma.supply}</strong>
                          {merma.lot === null ? null : (
                            <span>Lote {merma.lot}</span>
                          )}
                        </div>
                      </td>
                      <td className="numero">
                        {cantidad(merma.quantity.replace('-', ''))} {merma.unit}
                      </td>
                      <td>
                        <Pastilla
                          texto={
                            MOTIVO_DE_MERMA[merma.reason as WasteReason] ??
                            merma.reason ??
                            '—'
                          }
                          tono={
                            pideFirma(merma.reason as WasteReason)
                              ? 'peligro'
                              : 'neutro'
                          }
                        />
                      </td>
                      <td className="apagado">{merma.performedBy ?? 'Automático'}</td>
                      <td className="apagado mono">{dia(merma.occurredAt)}</td>
                      <td className="numero negativo">
                        {soles(Math.abs(Number.parseFloat(merma.value)))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <Tarjeta>
            <h2 className="tarjeta__titulo">Registrar merma</h2>

            <div className="formulario">
              {loteCargado === null ? null : (
                <p className="aviso-caja" role="status">
                  Dar de baja el lote <span className="mono">{loteCargado.code}</span> de{' '}
                  {loteCargado.supply}: va todo lo que queda. Revisa la cantidad
                  {motivo === 'expired' ? ' y firma' : ''}.
                </p>
              )}
              <div className="campo">
                <Rotulo htmlFor="insumo" ayuda="Lo que se perdió.">{mayuscula(vocabulario.insumo)}</Rotulo>
                <Buscable
                  id="insumo"
                  valor={supplyId}
                  onCambio={(insumoId) => {
                    setSupplyId(insumoId);
                    setLotId('');
                  }}
                  placeholder={`Busca el ${vocabulario.insumo} por nombre o código…`}
                  opciones={(insumos.data ?? []).map((insumo) =>
                    opcionDeInsumo(insumo, `quedan ${cantidad(insumo.quantity)} ${insumo.unit}`),
                  )}
                />
              </div>

              {elegido?.isLotTracked ? (
                <div className="campo">
                  <Rotulo htmlFor="merma-lote" ayuda="El lote exacto que se tira, si lo sabes. Si no, sale el que vence antes.">Qué lote</Rotulo>
                  <select id="merma-lote" value={lotId} onChange={(e) => setLotId(e.target.value)}>
                    <option value="">El que vence antes</option>
                    {(lotes.data ?? []).map((lote) => (
                      <option key={lote.id} value={lote.id}>
                        {lote.code} · quedan {cantidad(lote.quantity)}
                        {lote.expiresAt === null
                          ? ' · sin fecha'
                          : ` · vence el ${fechaDeCalendario(lote.expiresAt)} (${cuantoFalta(lote.expiresAt, diaDeLima())})`}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="campo">
                <Rotulo htmlFor="cuanto" ayuda={`Cuánto se perdió, en la unidad en que se cuenta el ${vocabulario.insumo}.`}>
                  Cantidad {elegido === undefined ? '' : `(${elegido.unit})`}
                </Rotulo>
                {/*
                 * En positivo: nadie escribe el signo de lo que está viendo
                 * perderse. El signo lo pone el servidor según el tipo.
                 */}
                <input
                  id="cuanto"
                  inputMode="decimal"
                  placeholder="2"
                  value={cuanto}
                  onChange={(e) => setCuanto(e.target.value)}
                />
              </div>

              <div>
                <p className="versal" style={{ marginBottom: 'var(--hueco-sm)' }}>Motivo</p>
                <div className="opciones">
                  {motivosDeMerma(tieneModulo(negocio, 'kitchen')).map((id) => (
                    <button
                      key={id}
                      type="button"
                      className={`opcion ${motivo === id ? 'opcion--activa' : ''}`}
                      onClick={() => setMotivo(id)}
                      aria-pressed={motivo === id}
                    >
                      {MOTIVO_DE_MERMA[id]}
                      {pideFirma(id) ? <Pastilla texto="firma" tono="aviso" /> : null}
                    </button>
                  ))}
                </div>
              </div>

              <p className={pideFirma(motivo) ? 'aviso-caja aviso-caja--aviso' : 'aviso-caja'}>
                {pideFirma(motivo)
                  ? 'Este motivo acusa a alguien —a la cámara o al proveedor— y no lo cierra en solitario quien lo descubre: lo firma con su PIN el encargado o el administrador, y el kardex guarda los dos nombres.'
                  : 'Pasa todos los días y lo registra quien estaba ahí. Queda en la bitácora del turno con su nombre.'}
              </p>

              {pideFirma(motivo) ? (
                <div className="campo">
                  <Rotulo htmlFor="pin" ayuda="Estos motivos acusan a alguien y los firma el encargado o el administrador con su PIN. Quedan los dos nombres en el historial.">PIN de quien autoriza</Rotulo>
                  <input
                    id="pin"
                    type="password"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={8}
                    placeholder="••••"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              ) : null}

              {registrar.isSuccess && firmadoPor !== null && pideFirma(motivo) ? (
                <p className="aviso-caja">Firmada por {firmadoPor}.</p>
              ) : null}

              {registrar.isError ? <Fallo error={registrar.error} /> : null}

              <Boton
                variante="peligro"
                onClick={() => registrar.mutate()}
                disabled={!puedeRegistrar}
              >
                {registrar.isPending ? 'Dando de baja…' : 'Dar de baja del stock'}
              </Boton>
            </div>
          </Tarjeta>
        </div>
      </div>
    </>
  );
}

/**
 * El motivo que más dinero se llevó, no el que más veces aparece.
 *
 * Diez servilletas rotas y un lomo vencido: por veces gana la rotura, y lo que
 * hay que mirar es la cámara.
 */
function motivoMasFrecuente(
  mermas: { reason: string | null; value: string }[],
): string {
  if (mermas.length === 0) return '—';

  const cuenta = new Map<string, number>();
  for (const merma of mermas) {
    if (merma.reason === null) continue;
    cuenta.set(
      merma.reason,
      (cuenta.get(merma.reason) ?? 0) + Math.abs(Number.parseFloat(merma.value)),
    );
  }

  const ganador = [...cuenta.entries()].sort((a, b) => b[1] - a[1])[0];

  return ganador === undefined
    ? '—'
    : (MOTIVO_DE_MERMA[ganador[0] as WasteReason] ?? ganador[0]);
}
